import { useEffect, useState } from "react";
import api, { API, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, FileDown, CheckCircle2, BookOpen, Wand2, UploadCloud, Send, Smartphone, Ban, Mail } from "lucide-react";
import { track } from "@/lib/analytics";
import { toast } from "sonner";

const REVIEW = ["draft", "scripture_verified", "content_reviewed", "approved"];
const REVIEW_LABEL = { draft: "Draft", scripture_verified: "Scripture verified", content_reviewed: "Content reviewed", approved: "Approved" };
const REVIEW_COLOR = { draft: "bg-slate-100 text-slate-600", scripture_verified: "bg-blue-100 text-blue-700", content_reviewed: "bg-amber-100 text-amber-700", approved: "bg-sage/15 text-sage" };
const COVER = ["not_created", "draft", "proof_ready", "approved"];
const PROOF = ["not_ready", "pdf_ready", "proof_ordered", "proof_received", "changes_required", "approved_for_sale"];
const nice = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const LULU_STEPS = [
  "Generate the final print-ready interior PDF (button above) and download it.",
  "Create a Lulu project at lulu.com.",
  "Select: US Letter · Coil bound · Black & white interior · White uncoated paper · Full-color matte cover.",
  "Upload the interior PDF.",
  "Create/upload the cover using Lulu's exact template (spine width depends on final page count).",
  "Order ONE proof copy shipped to the business owner.",
  "Inspect and test it (use the quality checklist).",
  "Only mark 'Approved for sale' after the proof passes every checklist item.",
];

export default function FaithAdmin() {
  const [slug] = useState("who-god-says-i-am");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(null);

  const load = () => api.get(`/admin/faith/books/${slug}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  if (!data) return <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber" /></div>;
  const { book, pages, status_counts = {}, total_pages } = data;
  const digitalSlug = book.digital_product_slug || "who-god-says-i-am";

  const run = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); if (msg) toast.success(msg); await load(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };
  const saveBook = (patch, msg) => run("book", () => api.put(`/admin/faith/books/${slug}`, patch), msg);

  return (
    <div className="space-y-6" data-testid="faith-admin">
      {/* Book header */}
      <div className="bg-white rounded-2xl border border-slate-100 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-amber" /><h3 className="font-display text-xl font-bold text-slateink">{book.title}</h3></div>
            <p className="text-sm text-slate-500 mt-1">{book.subtitle}</p>
            <p className="text-xs text-slate-400 mt-2">{book.print_format} · {book.binding} · {book.interior} · {book.cover_finish}</p>
            <p className="text-xs text-slate-400 mt-1">{book.attribution}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {REVIEW.map((r) => status_counts[r] ? <Badge key={r} className={`${REVIEW_COLOR[r]} border-0`}>{status_counts[r]} {REVIEW_LABEL[r]}</Badge> : null)}
            <Badge className="bg-slate-100 text-slate-600 border-0">{total_pages} pages</Badge>
          </div>
        </div>

        {/* Generation actions */}
        <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-slate-100">
          <Button variant="outline" size="sm" disabled={busy} data-testid="faith-approve-all"
            onClick={() => run("approve", () => api.post(`/admin/faith/books/${slug}/pages/approve-all`), "All pages approved")}>
            <CheckCircle2 className="w-4 h-4 mr-1" /> Approve all pages
          </Button>
          <Button size="sm" disabled={!!busy} data-testid="faith-gen-digital" className="bg-amber hover:bg-amber-600 text-white"
            onClick={() => run("digital", () => api.post(`/admin/faith/books/${slug}/generate-digital`), "Digital PDF generated — now review & approve, then activate")}>
            {busy === "digital" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />} Generate Digital PDF
          </Button>
          <Button size="sm" disabled={!!busy} data-testid="faith-gen-interior" variant="outline"
            onClick={() => run("interior", () => api.post(`/admin/faith/books/${slug}/generate-interior`), "Print interior PDF generated")}>
            {busy === "interior" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wand2 className="w-4 h-4 mr-1" />} Generate Print Interior PDF
          </Button>
          {book.digital_pdf && <Button asChild size="sm" variant="ghost" className="text-amber"><a href={`${API}/admin/faith/books/${slug}/download/digital`} target="_blank" rel="noreferrer" data-testid="faith-view-digital"><FileDown className="w-4 h-4 mr-1" /> Digital ({book.digital_pdf.page_count}p)</a></Button>}
          {book.print_interior_pdf && <Button asChild size="sm" variant="ghost" className="text-amber"><a href={`${API}/admin/faith/books/${slug}/download/interior`} target="_blank" rel="noreferrer" data-testid="faith-view-interior"><FileDown className="w-4 h-4 mr-1" /> Interior ({book.print_interior_pdf.page_count}p)</a></Button>}
        </div>
        <p className="text-xs text-slate-400 mt-2">Only <b>Approved</b> content pages are included in generated PDFs. Generating a PDF does <b>not</b> publish the product — review, approve and activate the digital edition below.</p>
      </div>

      {/* Digital edition activation */}
      <DigitalEdition slug={digitalSlug} bookSlug={slug} onChange={load} />

      {/* Pages */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 font-semibold text-slateink text-sm">Scripture & affirmation pages ({total_pages})</div>
        {pages.map((p) => (
          <button key={p.id} data-testid={`faith-page-${p.index}`} onClick={() => setEditing(p)} className="w-full flex items-center gap-3 px-5 py-3 border-b border-slate-50 hover:bg-subtle text-left">
            <span className="w-7 h-7 rounded-lg bg-cream text-amber-700 text-xs font-bold flex items-center justify-center shrink-0">{p.index}</span>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-slateink text-sm truncate">{p.title}</p>
              <p className="text-xs text-slate-400 truncate">{p.scripture_ref} · {p.translation}</p>
            </div>
            <Badge className={`${REVIEW_COLOR[p.review_status]} border-0 text-xs`}>{REVIEW_LABEL[p.review_status]}</Badge>
          </button>
        ))}
      </div>

      {/* Cover & proof status */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-slate-100 p-5 space-y-4">
          <h3 className="font-semibold text-slateink">Print cover status</h3>
          <Select value={book.cover_status} onValueChange={(v) => saveBook({ cover_status: v }, "Cover status updated")}>
            <SelectTrigger data-testid="faith-cover-status"><SelectValue /></SelectTrigger>
            <SelectContent>{COVER.map((c) => <SelectItem key={c} value={c}>{nice(c)}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-xs text-slate-400">Do not hard-code a spine width until the final page count and paper spec are confirmed with Lulu.</p>

          <h3 className="font-semibold text-slateink pt-2">Physical proof status</h3>
          <Select value={book.proof_status} onValueChange={(v) => saveBook({ proof_status: v }, "Proof status updated")}>
            <SelectTrigger data-testid="faith-proof-status"><SelectValue /></SelectTrigger>
            <SelectContent>{PROOF.map((c) => <SelectItem key={c} value={c}>{nice(c)}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-xs text-slate-400">The printed book stays hidden from customers until status is <b>Approved for sale</b> (requires all checklist items).</p>
          <ProofDetails book={book} onSave={(d) => saveBook({ proof_details: d }, "Proof details saved")} />
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-5">
          <h3 className="font-semibold text-slateink mb-3">Physical quality checklist</h3>
          <Checklist book={book} onSave={(cl) => saveBook({ proof_checklist: cl })} />
        </div>
      </div>

      {/* Lulu instructions */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5">
        <h3 className="font-semibold text-slateink mb-1">Order your first proof through Lulu (manual — Phase 1)</h3>
        <p className="text-xs text-slate-400 mb-3">The Lulu API is not integrated yet. Follow these steps manually.</p>
        <ol className="list-decimal pl-5 space-y-1.5 text-sm text-slate-600">
          {LULU_STEPS.map((s, i) => <li key={i}>{s}</li>)}
        </ol>
      </div>

      {editing && <PageEditor page={editing} onClose={() => setEditing(null)} reload={load} />}
    </div>
  );
}

const MANUAL_QC = [
  "Correct PDF uploaded", "File opens successfully", "No placeholder pages",
  "All pages present", "Branding correct", "Scripture/content reviewed",
];
const SYSTEM_QC = [
  { key: "download_accessed", label: "Download test successful" },
  { key: "email_sent", label: "Email delivery test successful" },
  { key: "account_access_verified", label: "Customer account access successful" },
  { key: "mobile_test_confirmed", label: "Mobile download tested" },
];
const TOTAL_QC = MANUAL_QC.length + SYSTEM_QC.length;
const DSTATUS_COLOR = {
  file_required: "bg-red-100 text-red-700", draft: "bg-slate-100 text-slate-600",
  ready_for_review: "bg-amber-100 text-amber-700", approved: "bg-blue-100 text-blue-700",
  active: "bg-sage/15 text-sage", disabled: "bg-slate-200 text-slate-600",
};
const dnice = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function DigitalEdition({ slug, bookSlug, onChange }) {
  const [prod, setProd] = useState(null);
  const [manualQc, setManualQc] = useState(MANUAL_QC.map(() => false));
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [test, setTest] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [grantAccount, setGrantAccount] = useState(true);
  const [sending, setSending] = useState(false);
  const load = () => api.get("/admin/products/digital").then((r) => setProd(r.data.find((x) => x.slug === slug) || null));
  const loadTest = () => api.get(`/admin/faith/books/${bookSlug}/test-delivery`).then((r) => setTest(r.data || null)).catch(() => {});
  useEffect(() => { load(); loadTest(); }, [slug]);
  if (!prod) return null;
  const asset = prod.asset;
  const systemOk = (k) => !!(test && test[k]);
  const allQc = manualQc.every(Boolean) && SYSTEM_QC.every((s) => systemOk(s.key));
  const act = async (path, msg, ok = true) => {
    setBusy(true);
    try { await api.post(`/admin/products/${prod.id}/${path}`); toast.success(msg); if (ok) track.digitalDownloadAvailable({ product_id: slug, product_type: "faith" }); await load(); onChange && onChange(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const onUpload = async (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post(`/admin/faith/books/${bookSlug}/upload-digital-source`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Attached "${r.data.original_filename}" (${r.data.page_count} pages) — Ready for review`);
      await load(); onChange && onChange();
    } catch (err) { toast.error(apiError(err)); } finally { setUploading(false); }
  };
  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error("Enter a test email you control"); return; }
    setSending(true);
    try {
      await api.post(`/admin/faith/books/${bookSlug}/test-delivery`, { test_email: testEmail.trim(), grant_account_access: grantAccount });
      toast.success("Test delivery sent — no payment was charged");
      setModalOpen(false);
      await loadTest();
    } catch (err) { toast.error(apiError(err)); } finally { setSending(false); }
  };
  const testAction = async (path, msg) => {
    setBusy(true);
    try { await api.post(`/admin/test-delivery/${test.id}/${path}`); toast.success(msg); await loadTest(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  const fmtSize = (b) => (b == null ? "—" : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1024 / 1024).toFixed(2)} MB`);
  const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");
  const YN = ({ ok }) => <b className={ok ? "text-sage" : "text-slate-400"}>{ok ? "Yes" : "No"}</b>;
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="faith-digital-edition">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-semibold text-slateink">Digital edition — activation</h3>
        <div className="flex gap-2">
          <Badge className={`${prod.file_required ? "bg-red-100 text-red-700" : "bg-sage/15 text-sage"} border-0`} data-testid="digital-file-status">
            {prod.file_required ? "File required" : `File: ${dnice(prod.file_status)}`}
          </Badge>
          <Badge className={`${DSTATUS_COLOR[prod.digital_status] || "bg-slate-100 text-slate-600"} border-0`} data-testid="digital-product-status">{dnice(prod.digital_status)}</Badge>
        </div>
      </div>
      <p className="text-xs text-slate-400 mt-2">A real approved PDF must exist before the ${prod.price} digital edition can go Active. Upload the final source PDF, run the test delivery, complete the checklist, Approve, then Activate.</p>

      {/* Attached file panel */}
      <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50/60 p-4" data-testid="digital-attached-file">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slateink">Attached file</span>
            {asset?.source_of_truth && <Badge className="bg-amber-100 text-amber-700 border-0" data-testid="digital-source-of-truth">Source of truth</Badge>}
          </div>
          <div className="flex gap-2">
            {asset && (
              <Button asChild size="sm" variant="ghost" className="text-amber" data-testid="digital-view-pdf">
                <a href={`${API}/admin/products/${prod.id}/asset`} target="_blank" rel="noreferrer"><FileDown className="w-4 h-4 mr-1" /> View PDF</a>
              </Button>
            )}
            <label data-testid="digital-upload-label">
              <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={onUpload} data-testid="digital-upload-input" />
              <Button asChild size="sm" variant="outline" disabled={uploading}>
                <span className="cursor-pointer">
                  {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <UploadCloud className="w-4 h-4 mr-1" />}
                  {asset ? "Replace PDF" : "Upload source PDF"}
                </span>
              </Button>
            </label>
          </div>
        </div>
        {asset ? (
          <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-slate-600" data-testid="digital-file-meta">
            <div><span className="text-slate-400">Filename:</span> <b>{asset.original_filename}</b></div>
            <div><span className="text-slate-400">Page count:</span> <b data-testid="digital-page-count">{asset.page_count ?? "—"}</b></div>
            <div><span className="text-slate-400">File size:</span> <b>{fmtSize(asset.file_size)}</b></div>
            <div><span className="text-slate-400">Uploaded:</span> <b>{fmtDate(asset.uploaded_at)}</b></div>
          </div>
        ) : (
          <p className="mt-2 text-xs text-slate-400">No file attached yet. Upload the final source-of-truth PDF to move to Ready for review.</p>
        )}
      </div>

      {/* Test digital delivery */}
      <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/40 p-4" data-testid="digital-test-delivery">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <span className="text-sm font-medium text-slateink">Test digital delivery</span>
            <p className="text-xs text-slate-500 mt-0.5">Safely verify email, secure download, account access and mobile — with no payment, no sale and no activation.</p>
          </div>
          <Button size="sm" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-100" disabled={!asset}
            data-testid="test-delivery-open" onClick={() => setModalOpen(true)}>
            <Send className="w-4 h-4 mr-1" /> Test digital delivery
          </Button>
        </div>

        {test && (
          <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3 text-xs" data-testid="test-results-panel">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-slateink">Test results</span>
              {test.revoked
                ? <Badge className="bg-slate-200 text-slate-600 border-0" data-testid="test-revoked-badge">Revoked</Badge>
                : test.expired
                  ? <Badge className="bg-slate-200 text-slate-600 border-0">Expired</Badge>
                  : <Badge className="bg-blue-100 text-blue-700 border-0">Active test</Badge>}
            </div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-slate-600">
              <div>Email sent: <YN ok={test.email_sent} /></div>
              <div>Recipient: <b data-testid="test-recipient-masked">{test.recipient_masked}</b></div>
              <div>Secure link generated: <YN ok={test.secure_link_generated} /></div>
              <div>Download accessed: <YN ok={test.download_accessed} /></div>
              <div>Download count: <b data-testid="test-download-count">{test.download_count}</b></div>
              <div>Account test entitlement: <b className={test.account_entitlement_granted ? "text-sage" : "text-slate-400"}>{test.account_entitlement_granted ? "Granted" : "Not granted"}</b></div>
              <div>Account access verified: <YN ok={test.account_access_verified} /></div>
              <div>Mobile test: <b className={test.mobile_test_confirmed ? "text-sage" : "text-amber-700"}>{test.mobile_test_confirmed ? "Confirmed" : "Pending"}</b></div>
              <div>Test entitlement expires: <b>{fmtDate(test.expires_at)}</b></div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-slate-100">
              <Button size="sm" variant="outline" disabled={busy || test.mobile_test_confirmed}
                data-testid="test-confirm-mobile" onClick={() => testAction("confirm-mobile", "Mobile download confirmed")}>
                <Smartphone className="w-4 h-4 mr-1" /> I tested it on my phone
              </Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50"
                disabled={busy || test.revoked} data-testid="test-revoke" onClick={() => testAction("revoke", "Test access revoked")}>
                <Ban className="w-4 h-4 mr-1" /> Revoke test access
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Quality checklist */}
      <div className="mt-4 grid sm:grid-cols-2 gap-2" data-testid="digital-checklist">
        {MANUAL_QC.map((label, i) => (
          <label key={label} data-testid={`digital-qc-manual-${i}`} className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
            <Checkbox checked={manualQc[i]} onCheckedChange={() => setManualQc(manualQc.map((v, x) => x === i ? !v : v))} className="mt-0.5" /> {label}
          </label>
        ))}
        {SYSTEM_QC.map((s) => (
          <div key={s.key} data-testid={`digital-qc-system-${s.key}`} className="flex items-start gap-2 text-sm text-slate-700">
            <Checkbox checked={systemOk(s.key)} disabled className="mt-0.5" />
            <span>{s.label} <span className="text-[10px] text-blue-500 uppercase tracking-wide">{s.key === "mobile_test_confirmed" ? "manual confirm" : "auto-verified"}</span></span>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
        <Button size="sm" disabled={busy || prod.file_required || !allQc || prod.digital_status === "approved" || prod.active}
          data-testid="digital-approve" onClick={() => act("digital/approve", "Digital file approved", false)}>
          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve file
        </Button>
        <Button size="sm" disabled={busy || prod.digital_status !== "approved"} className="bg-amber hover:bg-amber-600 text-white"
          data-testid="digital-activate" onClick={() => act("digital/activate", "Digital edition is now ACTIVE and on sale")}>
          Activate digital edition
        </Button>
        {prod.active && (
          <Button size="sm" variant="outline" disabled={busy} data-testid="digital-disable"
            onClick={() => act("digital/disable", "Digital edition disabled", false)}>Disable</Button>
        )}
        <div className="ml-auto flex items-center gap-3 text-xs text-slate-400">
          <span>Sales: {prod.total_sales}</span><span>Downloads: {prod.downloads}</span>
          {prod.fulfillment_errors > 0 && <span className="text-red-600 font-medium">Errors: {prod.fulfillment_errors}</span>}
        </div>
      </div>
      {!allQc && !prod.active && <p className="text-[11px] text-amber-700 mt-2" data-testid="digital-qc-hint">Complete all {TOTAL_QC} quality-check items to enable Approve. Download, email and account items are auto-verified by the test delivery; mobile requires your manual confirmation.</p>}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md" data-testid="test-delivery-modal">
          <DialogHeader><DialogTitle className="font-display text-xl">Test digital delivery</DialogTitle></DialogHeader>
          <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs font-semibold text-amber-800" data-testid="test-mode-banner">
            TEST MODE — NO PAYMENT WILL BE CHARGED
          </div>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> Test email</Label>
              <Input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@yourdomain.com"
                className="mt-1 h-10" data-testid="test-email-input" />
              <p className="text-[11px] text-slate-400 mt-1">Enter an inbox you control. The secure download link is sent here.</p>
            </div>
            <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer" data-testid="test-grant-account">
              <Checkbox checked={grantAccount} onCheckedChange={() => setGrantAccount(!grantAccount)} className="mt-0.5" />
              <span>Also grant a temporary test entitlement to my admin account, so I can verify the “My Account → Digital Downloads” flow.</span>
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" size="sm" onClick={() => setModalOpen(false)} data-testid="test-cancel">Cancel</Button>
            <Button size="sm" className="bg-amber hover:bg-amber-600 text-white" disabled={sending}
              data-testid="test-send" onClick={sendTest}>
              {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send test delivery
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProofDetails({ book, onSave }) {
  const [d, setD] = useState(book.proof_details || {});
  const f = (k, label, type = "text") => (
    <div><Label className="text-xs">{label}</Label><Input type={type} value={d[k] || ""} data-testid={`proof-${k}`} onChange={(e) => setD({ ...d, [k]: e.target.value })} className="mt-1 h-9" /></div>
  );
  return (
    <div className="pt-3 border-t border-slate-100 space-y-3">
      <p className="font-medium text-slateink text-sm">Proof record</p>
      {f("printer", "Printer")}
      <div className="grid grid-cols-2 gap-2">{f("proof_order_date", "Proof ordered", "date")}{f("proof_received_date", "Proof received", "date")}</div>
      <div className="grid grid-cols-2 gap-2">{f("production_cost", "Production cost ($)")}{f("shipping_cost", "Shipping cost ($)")}</div>
      <div><Label className="text-xs">Notes</Label><Textarea value={d.notes || ""} data-testid="proof-notes" onChange={(e) => setD({ ...d, notes: e.target.value })} className="mt-1" /></div>
      <Button size="sm" variant="outline" data-testid="proof-save" onClick={() => onSave(d)}>Save proof details</Button>
    </div>
  );
}

function Checklist({ book, onSave }) {
  const [cl, setCl] = useState(book.proof_checklist || []);
  const toggle = (i) => { const n = cl.map((c, x) => x === i ? { ...c, checked: !c.checked } : c); setCl(n); onSave(n); };
  const done = cl.filter((c) => c.checked).length;
  return (
    <div>
      <p className="text-xs text-slate-400 mb-2">{done}/{cl.length} complete — all required before approving for sale.</p>
      <div className="space-y-2">
        {cl.map((c, i) => (
          <label key={c.key} data-testid={`checklist-${c.key}`} className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
            <Checkbox checked={c.checked} onCheckedChange={() => toggle(i)} className="mt-0.5" /> {c.label}
          </label>
        ))}
      </div>
    </div>
  );
}

function PageEditor({ page, onClose, reload }) {
  const [p, setP] = useState(page);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setP({ ...p, [k]: v });
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/faith/pages/${page.id}`, {
        title: p.title, scripture_ref: p.scripture_ref, translation: p.translation,
        scripture_text: p.scripture_text, explanation: p.explanation, affirmation: p.affirmation,
        prayer: p.prayer, activity: p.activity, review_status: p.review_status, review_notes: p.review_notes,
      });
      toast.success("Page saved"); reload(); onClose();
    } catch (err) { toast.error(apiError(err)); setBusy(false); }
  };
  const field = (k, label, rows = 2) => (
    <div><Label className="text-xs">{label}</Label><Textarea rows={rows} value={p[k] || ""} data-testid={`page-field-${k}`} onChange={(e) => set(k, e.target.value)} className="mt-1" /></div>
  );
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="faith-page-editor">
        <DialogHeader><DialogTitle className="font-display text-xl">Affirmation {p.index}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Affirmation title</Label><Input value={p.title || ""} data-testid="page-field-title" onChange={(e) => set("title", e.target.value)} className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Scripture reference</Label><Input value={p.scripture_ref || ""} data-testid="page-field-ref" onChange={(e) => set("scripture_ref", e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs">Translation</Label><Input value={p.translation || ""} onChange={(e) => set("translation", e.target.value)} className="mt-1" /></div>
          </div>
          {field("scripture_text", "Quoted Scripture text (WEB)", 3)}
          <p className="text-[11px] text-amber-700">Only replace WEB text with a copyrighted translation after confirming appropriate permissions. Never alter a quotation's meaning.</p>
          {field("explanation", "What God's Word teaches me")}
          {field("affirmation", "My affirmation")}
          {field("prayer", "My prayer")}
          {field("activity", "My activity")}
          {field("review_notes", "Review notes", 2)}
          <div>
            <Label className="text-xs">Review status</Label>
            <Select value={p.review_status} onValueChange={(v) => set("review_status", v)}>
              <SelectTrigger data-testid="page-review-status" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{REVIEW.map((r) => <SelectItem key={r} value={r}>{REVIEW_LABEL[r]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="faith-page-save" className="bg-amber hover:bg-amber-600 text-white">{busy ? "Saving…" : "Save page"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
